import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ButtonWithLoader from 'commons/containers/ButtonWithLoader';
import DatePicker from 'commons/components/DatePicker';
import TimePicker from 'commons/components/TimePicker';

function BookingForm({
  isOpen,
  toggleForm,
  controls,
  changeControls,
  submit,
  invalidControls,
  suppliers = [],
  operationTypes = [],
  truckTypes = [],
  loadConditions = [],
  edit = false,
  complete,
  objects,
}) {
  const { t } = useTranslation();
  const [varieties, setVarieties] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [processingSites, setProcessingSites] = useState([]);
  const [lastPickupCarrierId, setLastPickupCarrierId] = useState(controls.carrierId || '');

  useEffect(() => {
    if (isOpen && edit && controls.operationType === 'PICKUP') {
        setLastPickupCarrierId(controls.carrierId || '');
    }
  }, [isOpen, edit]);

  useEffect(() => {
    let isMounted = true;
    const fetchVarieties = () => {
      const headers = { 'Content-Type': 'application/json' };
      fetch(`${window.location.origin}/api/v1/varieties`, { method: 'GET', headers })
        .then((res) => res.json())
        .then((response) => {
          if (isMounted && response && response.objects && response.objects.varieties) {
            setVarieties(Object.values(response.objects.varieties));
          }
        })
        .catch((err) => {
            if (isMounted) console.error('Error fetching varieties:', err);
        });
    };

    const fetchCarriers = () => {
      const headers = { 'Content-Type': 'application/json' };
      fetch(`${window.location.origin}/api/v1/carriers`, { method: 'GET', headers })
        .then((res) => res.json())
        .then((response) => {
          if (isMounted && response && response.objects && response.objects.carriers) {
            setCarriers(Object.values(response.objects.carriers));
          }
        })
        .catch((err) => {
            if (isMounted) console.error('Error fetching carriers:', err);
        });
    };

    const fetchProcessingSites = () => {
      const headers = { 'Content-Type': 'application/json' };
      fetch(`${window.location.origin}/api/v1/processingSites`, { method: 'GET', headers })
        .then((res) => res.json())
        .then((response) => {
          if (isMounted && response && response.objects && response.objects.processingSites) {
            setProcessingSites(Object.values(response.objects.processingSites));
          }
        })
        .catch((err) => {
            if (isMounted) console.error('Error fetching processing sites:', err);
        });
    };

    fetchVarieties();
    fetchCarriers();
    fetchProcessingSites();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleDateChange = (event, value) => {
    let dateValue = value;
    if (typeof event === 'string') {
        dateValue = event;
    } else if (value) {
        dateValue = value;
    } else if (event && event.target) {
        dateValue = event.target.value;
    }
    
    changeControls({ target: { id: 'date', value: dateValue } });
  };

  const handleTruckTypeChange = (event) => {
    const truckType = event.target.value;
    const updates = { truckType };

    if (truckType === 'BATEA') {
      updates.loadConditions = 'HUMEDO';
      updates.binsQty = 60;
    } else if (truckType === 'AMPLIROLL') {
      updates.loadConditions = 'HUMEDO';
      updates.binsQty = 30;
    } else if (truckType === 'PISO_CAMINANTE') {
      updates.loadConditions = 'HUMEDO';
      updates.binsQty = 90;
    } else if (truckType === 'CAMION_PLANO') {
      updates.binsQty = "";
    }

    changeControls(updates);
  };

  const handleChange = (id) => (event) => {
    const value = event.target.value;
    if (id === 'operationType') {
      const updates = { [id]: value };
      if (value === 'DISPATCH') {
        updates.needCarrier = false;
        const externalCarrier = carriers.find((c) => c.name.toUpperCase() === 'EXTERNO');
        if (externalCarrier) {
          updates.carrierId = externalCarrier.id;
        }
      } else if (value === 'PICKUP') {
        updates.needCarrier = true;
        updates.carrierId = lastPickupCarrierId;
      }
      changeControls(updates);
    } else {
      if (id === 'carrierId' && controls.operationType === 'PICKUP') {
        setLastPickupCarrierId(value);
      }
      changeControls({ target: { id, value } });
    }
  };

  const handleCheckboxChange = (id) => (event) => {
    changeControls({ target: { id, value: event.target.checked } });
  };

  const handleComplete = () => {
    complete();
  };

  return (
    <Dialog open={isOpen} onClose={() => toggleForm()} fullWidth maxWidth="md">
      <DialogTitle>{edit ? t('scheduleManagement.form.editTitle') : t('scheduleManagement.form.addTitle')}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label={t('scheduleManagement.form.operationType')}
              value={controls.operationType || ''}
              onChange={handleChange('operationType')}
              error={invalidControls.operationType}
              helperText={invalidControls.operationType && t('Field required', { field: t('scheduleManagement.form.operationType') })}
            >
              {[
                { id: 'PICKUP', name: t('scheduleManagement.operationTypes.pickup') },
                { id: 'DISPATCH', name: t('scheduleManagement.operationTypes.dispatch') },
              ].map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) => option.name || ''}
              value={
                controls.supplierId
                  ? suppliers.find((s) => s.id === controls.supplierId) || null
                  : null
              }
              onChange={(e, val) => {
                changeControls({ target: { id: 'supplierId', value: val ? val.id : null } });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  label={t('Proveedor')}
                  error={invalidControls.supplierId}
                  helperText={invalidControls.supplierId && t('Field required', { field: t('Proveedor') })}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label={t('Variedad')}
              value={controls.varietyId || ''}
              onChange={handleChange('varietyId')}
              error={invalidControls.varietyId}
              helperText={invalidControls.varietyId && t('Field required', { field: t('Variedad') })}
            >
              {varieties.map((v) => (
                <MenuItem key={v.id} value={v.id}>
                  {v.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
                <DatePicker
                    fullWidth
                    label={t('Fecha de agendamiento')}
                    value={controls.date}
                    onChange={handleDateChange}
                    error={invalidControls.date}
                    helperText={invalidControls.date && t('Field required', { field: t('Fecha de agendamiento') })}
                    name="date"
                    inputId="date"
                    type="date"
                  />
          </Grid>
          {controls.operationType !== 'DISPATCH' && (
            <Grid item xs={12} sm={6}>
                <TimePicker
                    fullWidth
                    label={t('Llegada al campo')}
                    value={controls.arrivalFieldAt}
                    onChange={(e, val) => {
                      let d = val || e?.target?.value || e;
                      changeControls({ target: { id: 'arrivalFieldAt', value: d } });
                    }}
                    error={invalidControls.arrivalFieldAt}
                    helperText={invalidControls.arrivalFieldAt && t('Field required', { field: t('Llegada al campo') })}
                    name="arrivalFieldAt"
                    inputId="arrivalFieldAt"
                  />
            </Grid>
          )}
           <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
                label={t('scheduleManagement.form.truckType')}
                value={controls.truckType || ''}
                onChange={handleTruckTypeChange}
                error={invalidControls.truckType}
                helperText={invalidControls.truckType && t('Field required', { field: t('scheduleManagement.form.truckType') })}
              >
                 {[
                   { id: 'Batea', name: t('scheduleManagement.truckTypes.batea') },
                   { id: 'Ampliroll', name: t('scheduleManagement.truckTypes.ampliroll') },
                   { id: 'Piso caminante', name: t('scheduleManagement.truckTypes.piso_caminante') },
                   { id: 'Camion plano', name: t('scheduleManagement.truckTypes.camiones_planos') },
                 ].map((item) => (
                <MenuItem key={item.id} value={item.id.toUpperCase().replace(/ /g, '_')}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
                label={t('scheduleManagement.form.loadType')}
                value={controls.loadConditions || ''}
                onChange={handleChange('loadConditions')}
                error={invalidControls.loadConditions}
                helperText={invalidControls.loadConditions && t('Field required', { field: t('scheduleManagement.form.loadType') })}
              >
                 {[
                   { id: 'HUMEDO', name: t('HUMEDO') },
                   { id: 'SECO', name: t('SECO') },
                 ].map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label={t('Bins con fruta')}
              value={controls.binsQty !== undefined ? controls.binsQty : ''}
              onChange={handleChange('binsQty')}
              error={invalidControls.binsQty}
              helperText={invalidControls.binsQty && t('Field required', { field: t('Bins con fruta') })}
              InputLabelProps={{ shrink: (controls.binsQty !== undefined && controls.binsQty !== null && controls.binsQty !== '') ? true : undefined }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label={t('Bins vacíos')}
              value={controls.emptyBins || ''}
              onChange={handleChange('emptyBins')}
              error={invalidControls.emptyBins}
              helperText={invalidControls.emptyBins && t('Field required', { field: t('Bins vacíos') })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              type="number"
              label={t('Retorno de Bins')}
              value={controls.returnBins || ''}
              onChange={handleChange('returnBins')}
              error={invalidControls.returnBins}
              helperText={invalidControls.returnBins && t('Field required', { field: t('Retorno de Bins') })}
            />
          </Grid>
          {controls.truckType === 'CAMION_PLANO' && controls.loadConditions === 'SECO' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="number"
                label={t('scheduleManagement.form.maxisacos')}
                value={controls.superSack || ''}
                onChange={handleChange('superSack')}
                error={invalidControls.superSack}
                helperText={invalidControls.superSack && t('Field required', { field: t('scheduleManagement.form.maxisacos') })}
              />
            </Grid>
          )}
          {true && (
            <Grid item xs={12} sm={6}>
              <TimePicker
                fullWidth
                label={t('Llegada a planta')}
                value={controls.arrivalProcessingPlant}
                onChange={(e, val) => {
                  let d = val || e?.target?.value || e;
                  changeControls({ target: { id: 'arrivalProcessingPlant', value: d } });
                }}
                error={invalidControls.arrivalProcessingPlant}
                helperText={invalidControls.arrivalProcessingPlant && t('Field required', { field: t('Llegada a planta') })}
                name="arrivalProcessingPlant"
                inputId="arrivalProcessingPlant"
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label={t('scheduleManagement.form.processingSite')}
              value={controls.processingSiteId || ''}
              onChange={handleChange('processingSiteId')}
              error={invalidControls.processingSiteId}
              helperText={invalidControls.processingSiteId && t('Field required', { field: t('scheduleManagement.form.processingSite') })}
            >
              <MenuItem value="">{t('None') || 'Ninguno'}</MenuItem>
              {processingSites.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {controls.operationType !== 'DISPATCH' && (
            <Grid item xs={12} sm={6} style={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!controls.needCarrier}
                    onChange={handleCheckboxChange('needCarrier')}
                    color="primary"
                  />
                }
                label={t('scheduleManagement.form.needCarrier')}
              />
            </Grid>
          )}
          {controls.needCarrier && (
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                label={t('scheduleManagement.form.carrier')}
                value={controls.carrierId || ''}
                onChange={handleChange('carrierId')}
                error={invalidControls.carrierId}
                helperText={invalidControls.carrierId && t('Field required', { field: t('scheduleManagement.form.carrier') })}
              >
                <MenuItem value="">{t('None') || 'Ninguno'}</MenuItem>
                {carriers.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between' }}>
        {edit ? (
            <Button onClick={handleComplete} color="default" variant="outlined" style={{ borderColor: '#4caf50', color: '#4caf50' }}>
                {t('Finalizar Agendamiento')}
            </Button>
        ) : <div />}
        <div>
            <Button onClick={() => toggleForm()} color="primary" style={{ marginRight: '8px' }}>
            {t('CANCEL')}
            </Button>           
            <ButtonWithLoader onClick={submit} color="primary" autoFocus>
            {edit ? t('UPDATE') : t('ADD')}
            </ButtonWithLoader>
        </div>
      </DialogActions>
    </Dialog>
  );
}

export default BookingForm;
